import { useEffect, useState } from 'react'
import { getNewsletters, markNewsletterRead } from '../lib/supabase'
import { getDrukteData, bepaalDrukteNiveau } from '../lib/drukte'
import { isNuOpen } from '../lib/openingstijden'
import MeldingenKaart from '../components/MeldingenKaart'

// ── Drukte-gauge ─────────────────────────────────────────
// Eén doorlopende, dun-naar-dik aflopende boog (geen stoplichtkleuren) met een
// wijzer die van dun (rustig) naar dik (druk) beweegt over dezelfde boog.
// Opgebouwd uit veel kleine, licht overlappende lijnsegmenten i.p.v. canvas,
// zodat het altijd vloeiend en betrouwbaar rendert.
function lerp(a, b, t) { return a + (b - a) * t }
function lerpKleur(hexA, hexB, t) {
  const a = hexA.match(/\w\w/g).map(x => parseInt(x, 16))
  const b = hexB.match(/\w\w/g).map(x => parseInt(x, 16))
  return `rgb(${a.map((v, i) => Math.round(lerp(v, b[i], t))).join(',')})`
}
const GAUGE_SEGMENTEN = 60
const GAUGE_LICHT = '#dbe3d5'
const GAUGE_DONKER = '#3f4f3d'

function DrukteGauge({ ratio }) {
  const cx = 120, cy = 116, r = 90
  const minDikte = 4, maxDikte = 20
  const t = Math.max(0, Math.min(1, ratio))
  const naaldHoekRad = ((180 - t * 180) * Math.PI) / 180
  const naaldLengte = r - 8
  const naaldX = cx + naaldLengte * Math.cos(naaldHoekRad)
  const naaldY = cy - naaldLengte * Math.sin(naaldHoekRad)

  const segmenten = []
  for (let i = 0; i < GAUGE_SEGMENTEN; i++) {
    const t0 = Math.max(0, i / GAUGE_SEGMENTEN - 0.004)
    const t1 = Math.min(1, (i + 1) / GAUGE_SEGMENTEN + 0.004)
    const tm = (i + 0.5) / GAUGE_SEGMENTEN
    const hoek0 = ((180 - t0 * 180) * Math.PI) / 180
    const hoek1 = ((180 - t1 * 180) * Math.PI) / 180
    segmenten.push(
      <line
        key={i}
        x1={cx + r * Math.cos(hoek0)} y1={cy - r * Math.sin(hoek0)}
        x2={cx + r * Math.cos(hoek1)} y2={cy - r * Math.sin(hoek1)}
        stroke={lerpKleur(GAUGE_LICHT, GAUGE_DONKER, tm)}
        strokeWidth={lerp(minDikte, maxDikte, tm)}
        strokeLinecap="round"
      />
    )
  }

  return (
    <svg viewBox="0 0 240 132" style={{ width: '100%', maxWidth: 220, display: 'block', margin: '0 auto' }}>
      {segmenten}
      <line x1={cx} y1={cy} x2={naaldX} y2={naaldY} stroke="var(--green-dark)" strokeWidth={4} strokeLinecap="round" />
      <circle cx={cx} cy={cy} r={6.5} fill="var(--green-dark)" />
    </svg>
  )
}

// ── Verwachting per uur (stipjes) ────────────────────────
// Vat de half-uur-verwachting samen tot één waarde per uur (som van de twee
// halve uren = verwachte check-ins in dat uur) en toont die als afgeronde
// staafjes onder de meter, zoals Google "populaire tijden". Dit is altijd de
// historische verwachting voor deze weekdag, ook voor uren die al voorbij
// zijn — wat er nú gebeurt laat de meter zien.
function bouwUrenSamenvatting(data) {
  const perUur = {}
  data.forEach(d => {
    const uur = Number(d.blok.split(':')[0])
    if (!perUur[uur]) perUur[uur] = { verwachting: 0, gesloten: true }
    if (!d.gesloten) {
      perUur[uur].verwachting += d.verwachting
      perUur[uur].gesloten = false
    }
  })
  return Object.keys(perUur)
    .map(Number)
    .sort((a, b) => a - b)
    .map(uur => ({ uur, ...perUur[uur] }))
}

// Woord bij een staafje, met dezelfde grenzen als de meter. Check-ins per uur
// × 0,75 ≈ bezetting in een venster van 45 minuten (zie bepaalDrukteNiveau).
function verwachtWoord(checkinsPerUur) {
  return bepaalDrukteNiveau(null, checkinsPerUur * 0.75).label.toLowerCase()
}

const STAAF_LICHT = lerpKleur(GAUGE_LICHT, GAUGE_DONKER, 0.35)
const NU_KLEUR = '#e76622'

function DrukteStaafjes({ data, open }) {
  const uren = bouwUrenSamenvatting(data)
  const nuUur = new Date().getHours()
  const nuIndex = open ? uren.findIndex(u => u.uur === nuUur && !u.gesloten) : -1
  const [gekozen, setGekozen] = useState(null)
  if (uren.length === 0) return null

  const stap = 22
  const breedte = uren.length * stap
  const basislijn = 66
  const maxHoogte = 54
  const piek = Math.max(...uren.map(u => u.verwachting), 0.1)
  const drukste = uren.reduce((best, u) => (u.verwachting > best.verwachting ? u : best), uren[0])

  const geselecteerd = gekozen ?? (nuIndex >= 0 ? uren[nuIndex].uur : null)
  const keuze = uren.find(u => u.uur === geselecteerd)
  let kop
  if (keuze && keuze.uur === nuUur && nuIndex >= 0) {
    kop = <><b style={{ color: 'var(--green-dark)' }}>Rond deze tijd</b> · meestal {verwachtWoord(keuze.verwachting)}</>
  } else if (keuze) {
    kop = <><b style={{ color: 'var(--green-dark)' }}>{keuze.uur}:00</b> · meestal {verwachtWoord(keuze.verwachting)}</>
  } else {
    kop = <>Meestal het drukst rond <b style={{ color: 'var(--green-dark)' }}>{drukste.uur}:00</b></>
  }

  // Aaneengesloten gesloten uren (bv. de lunchpauze) krijgen samen één
  // lichtgrijs woordje "gesloten" in het midden.
  const geslotenReeksen = []
  uren.forEach((u, i) => {
    if (!u.gesloten) return
    const laatste = geslotenReeksen[geslotenReeksen.length - 1]
    if (laatste && laatste.eind === i - 1) laatste.eind = i
    else geslotenReeksen.push({ start: i, eind: i })
  })

  return (
    <>
      <p style={{ fontFamily: 'Montserrat', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--muted)', margin: '0 0 2px', textAlign: 'center' }}>
        Verwachting per uur
      </p>
      <p style={{ fontSize: 12, color: 'var(--text)', textAlign: 'center', margin: '0 0 4px', minHeight: 18 }}>
        {kop}
      </p>
      <svg viewBox={`0 0 ${breedte} 80`} style={{ width: '100%', display: 'block' }}>
        <line x1={0} y1={basislijn} x2={breedte} y2={basislijn} stroke="var(--border)" strokeWidth={1} />
        {geslotenReeksen.filter(r => r.eind > r.start).map(r => (
          <text key={r.start} x={((r.start + r.eind) / 2) * stap + stap / 2} y={basislijn - 5} fontSize={8} fill="#a3a39a" textAnchor="middle">
            gesloten
          </text>
        ))}
        {uren.map((u, i) => {
          const x = i * stap + stap / 2
          const vorigeGesloten = i > 0 && uren[i - 1].gesloten
          // Label bij opening, sluiting en de overgangen (zoals de lunchpauze).
          const toonLabel = (i === 0 || i === uren.length - 1 || u.gesloten !== vorigeGesloten) && !(u.gesloten && vorigeGesloten)
          const hoogte = Math.max(14, maxHoogte * (u.verwachting / piek))
          const isGekozen = u.uur === geselecteerd
          return (
            <g key={u.uur}>
              {!u.gesloten && (
                <rect
                  x={x - 6} y={basislijn - hoogte} width={12} height={hoogte - 2} rx={6}
                  fill={isGekozen ? GAUGE_DONKER : STAAF_LICHT}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setGekozen(u.uur)}
                />
              )}
              {i === nuIndex && (
                <text x={x} y={basislijn - hoogte - 5} fontSize={8} fontWeight={700} fill={NU_KLEUR} textAnchor="middle">nu</text>
              )}
              {toonLabel && (
                <text x={x} y={basislijn + 12} fontSize={8.5} fill="var(--muted)" textAnchor="middle">{u.uur}u</text>
              )}
            </g>
          )
        })}
      </svg>
    </>
  )
}

import { renderLinks } from '../utils/renderLinks'

const CATEGORY_LABELS = {
  announcement: 'Aankondiging',
  letop: 'Let op!',
  news: 'Nieuws',
  event: 'Evenement',
  diversen: 'Diversen',
}

// Accentkleur per categorie — geeft elk nieuwsbericht een herkenbare spine
const CATEGORY_ACCENT = {
  announcement: 'var(--green)',
  letop: '#c0392b',
  news: '#1a56a0',
  event: 'var(--orange)',
  diversen: '#6b4fa0',
}

function DrukteBlok() {
  const [data, setData] = useState([])
  const [live, setLive] = useState(false)
  const [bezettingNu, setBezettingNu] = useState(null)

  useEffect(() => {
    function laad() {
      getDrukteData()
        .then(({ data, live, bezettingNu }) => { setData(data); setLive(live); setBezettingNu(bezettingNu) })
        .catch(() => setData([]))
    }
    laad()
    // Elke 5 minuten opnieuw laden, zodat zowel de cijfers als de gemeten/verwacht-
    // indeling blijven kloppen als een sessie lang open blijft staan (anders blijft
    // "huidig uur" hangen op het moment dat de app geopend werd).
    const interval = setInterval(laad, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  if (data.length === 0) return null

  const open = isNuOpen()
  const niveau = open ? bepaalDrukteNiveau(data, bezettingNu) : { label: 'Gesloten', ratio: 0 }

  return (
    <div className="card" style={{ marginBottom: 4 }}>
      <p style={{ fontFamily: 'Montserrat', fontSize: 13, fontWeight: 700, margin: '0 0 8px' }}>
        Drukte in de club
      </p>

      <DrukteGauge ratio={niveau.ratio} />
      <p style={{ textAlign: 'center', fontFamily: 'Montserrat', fontWeight: 700, fontSize: 15, color: 'var(--green-dark)', margin: '2px 0 14px' }}>
        Nu: {niveau.label}
      </p>

      <DrukteStaafjes data={data} open={open} />

      {open && !live && (
        <p style={{ fontSize: 10.5, color: 'var(--muted)', textAlign: 'center', fontStyle: 'italic', marginTop: 6 }}>
          Voorbeelddata — live cijfers volgen zodra de koppeling met Sportivity actief is.
        </p>
      )}
    </div>
  )
}

export default function HomeTab({ member }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [expandedIds, setExpandedIds] = useState(new Set())

  const voornaam = member?.name?.split(' ')[0] || ''

  useEffect(() => {
    getNewsletters()
      .then(data => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }, [])

  function toggleItem(item) {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(item.id)) {
        next.delete(item.id)
      } else {
        next.add(item.id)
        markNewsletterRead(item.id, member?.member_id)
      }
      return next
    })
  }

  return (
    <div>
      <MeldingenKaart lidId={member.id} />

      {/* Welkomstbericht */}
      <div className="card" style={{ background: 'var(--green)', marginBottom: 4 }}>
        <p style={{ fontFamily: 'Montserrat', fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.75)', marginBottom: 4 }}>
          Welkom terug
        </p>
        <p style={{ fontFamily: 'Montserrat', fontSize: 22, fontWeight: 600, color: '#fff', lineHeight: 1.2 }}>
          Blijf Vitaal Sterk,<br />{voornaam}!
        </p>
      </div>

      {/* Status open/gesloten */}
      {(() => {
        const open = isNuOpen()
        return (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: open ? 'rgba(76,153,80,0.12)' : 'rgba(200,50,50,0.08)',
            border: `1px solid ${open ? 'var(--green)' : '#e55'}`,
            borderRadius: 10, padding: '8px 14px', marginBottom: 4,
          }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: open ? 'var(--green)' : '#e55',
              flexShrink: 0,
            }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: open ? 'var(--green-dark)' : '#c33' }}>
              {open ? 'Wij zijn nu open' : 'Wij zijn nu gesloten'}
            </span>
          </div>
        )
      })()}

      {/* Drukte */}
      <DrukteBlok />

      {/* Nieuws */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 4px 8px' }}>
        <p style={{ fontFamily: 'Montserrat', fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
          Laatste nieuws
        </p>
      </div>

      {loading && <div className="spinner" />}

      {!loading && items.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '32px 16px' }}>
          <p style={{ color: 'var(--muted)', fontSize: 13 }}>Nog geen nieuws geplaatst.</p>
        </div>
      )}

      {!loading && items.map(item => {
        const open = expandedIds.has(item.id)
        return (
          <div
            key={item.id}
            className="card"
            style={{
              padding: 0, overflow: 'hidden',
              marginBottom: 14,
              borderLeft: `4px solid ${CATEGORY_ACCENT[item.category] || 'var(--green)'}`,
              boxShadow: '0 2px 12px rgba(30,36,22,0.07)',
            }}
          >
            {item.foto_url && (
              <img
                src={item.foto_url}
                alt={item.title}
                style={{ width: '100%', maxHeight: 180, objectFit: 'cover', display: 'block' }}
              />
            )}
            <div
              style={{ padding: '14px 16px', cursor: 'pointer' }}
              onClick={() => toggleItem(item)}
            >
              <span className={`badge badge-${item.category}`}>
                {CATEGORY_LABELS[item.category] || item.category}
              </span>
              <p className="card-title">{item.title}</p>
              <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>
                {new Date(item.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>

              {open ? (
                <p style={{ fontSize: 14, lineHeight: 1.8, color: 'var(--text)' }}>
                  {renderLinks(item.body)}
                </p>
              ) : (
                <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--muted)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                  {item.body}
                </p>
              )}

              <p style={{
                fontSize: 12, color: 'var(--green-dark)', fontWeight: 700, marginTop: 8, fontFamily: 'Montserrat',
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                {open ? 'Lees minder' : 'Lees meer'}
                <span style={{
                  display: 'inline-block',
                  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}>
                  ▾
                </span>
              </p>
            </div>
          </div>
        )
      })}
    </div>
  )
}
